const toBoolean = (value) => value === true || value === 'true' || value === 1 || value === '1'

const getPermissionValue = (permissions, snakeKey, camelKey) => (
  permissions?.[snakeKey] !== undefined ? permissions[snakeKey] : permissions?.[camelKey]
)

const getPermissionSource = (source = {}) => {
  if (source?.permissions) return source.permissions

  const firstInventory = Array.isArray(source?.inventories) ? source.inventories[0] : null
  if (firstInventory?.permissions) return firstInventory.permissions

  return source || {}
}

export const normalizeInventoryPricePermissions = (source = {}) => {
  const permissions = getPermissionSource(source)
  const canViewPrices = toBoolean(getPermissionValue(permissions, 'can_view_prices', 'canViewPrices'))
  const canViewSupplierNameValue = getPermissionValue(permissions, 'can_view_supplier_name', 'canViewSupplierName')

  const canViewDefaultPriceValue = getPermissionValue(permissions, 'can_view_default_price', 'canViewDefaultPrice')
  const canViewSellingPriceValue = getPermissionValue(permissions, 'can_view_selling_price', 'canViewSellingPrice')
  const canViewBuyingPrice = toBoolean(getPermissionValue(permissions, 'can_view_buying_price', 'canViewBuyingPrice'))
  const canViewDefaultPrice = canViewDefaultPriceValue === undefined ? canViewPrices : toBoolean(canViewDefaultPriceValue)
  const canViewSellingPrice = canViewSellingPriceValue === undefined ? canViewPrices : toBoolean(canViewSellingPriceValue)
  const canEditBuyingPriceValue = getPermissionValue(permissions, 'can_edit_buying_price', 'canEditBuyingPrice')
  const canEditDefaultPriceValue = getPermissionValue(permissions, 'can_edit_default_price', 'canEditDefaultPrice')
  const canEditSellingPriceValue = getPermissionValue(permissions, 'can_edit_selling_price', 'canEditSellingPrice')

  return {
    canViewPrices,
    canViewCosts: toBoolean(getPermissionValue(permissions, 'can_view_costs', 'canViewCosts')),
    canViewDefaultPrice,
    canViewSellingPrice,
    canViewBuyingPrice,
    canEditBuyingPrice    : canEditBuyingPriceValue === undefined ? canViewBuyingPrice : toBoolean(canEditBuyingPriceValue),
    canEditDefaultPrice   : canEditDefaultPriceValue === undefined ? canViewDefaultPrice : toBoolean(canEditDefaultPriceValue),
    canEditSellingPrice   : canEditSellingPriceValue === undefined ? canViewSellingPrice : toBoolean(canEditSellingPriceValue),
    canViewSupplierName   : canViewSupplierNameValue === undefined ? true : toBoolean(canViewSupplierNameValue),
    canSetSellingPriceBelowBuyingPrice: toBoolean(getPermissionValue(permissions, 'can_set_selling_price_below_buying_price', 'canSetSellingPriceBelowBuyingPrice')),
    canImportProductCosts : toBoolean(getPermissionValue(permissions, 'can_import_product_costs', 'canImportProductCosts'))
  }
}

export const normalizeInventoryEditPermissions = (source = {}) => {
  const permissions = getPermissionSource(source)
  const canEditAllProductFields = toBoolean(getPermissionValue(permissions, 'can_edit_all_product_fields', 'canEditAllProductFields'))
  const rawEditableProductFields = getPermissionValue(permissions, 'editable_product_fields', 'editableProductFields')
  const editableProductFields = Array.isArray(rawEditableProductFields)
    ? rawEditableProductFields.map((field) => String(field))
    : []
  const canEditProductCuratorPicValue = getPermissionValue(permissions, 'can_edit_product_curator_pic', 'canEditProductCuratorPic')
  const rawEditablePriceFields = getPermissionValue(permissions, 'editable_price_fields', 'editablePriceFields')
  const legacyCanEditPrices = toBoolean(getPermissionValue(permissions, 'can_edit_prices', 'canEditPrices'))
  const editablePriceFields = Array.isArray(rawEditablePriceFields)
    ? rawEditablePriceFields.map((field) => String(field))
    : canEditAllProductFields || legacyCanEditPrices
      ? [ 'buying_price', 'default_price', 'selling_price' ]
      : []
  const canEditPrices = editablePriceFields.length > 0
  const canEditSpecifications = toBoolean(getPermissionValue(permissions, 'can_edit_specifications', 'canEditSpecifications')) || canEditAllProductFields
  const canEditLocations = toBoolean(getPermissionValue(permissions, 'can_edit_locations', 'canEditLocations')) || canEditAllProductFields
  const canEditPhotos = toBoolean(getPermissionValue(permissions, 'can_edit_photos', 'canEditPhotos')) || canEditAllProductFields
  const canEditProduct = toBoolean(getPermissionValue(permissions, 'can_edit_product', 'canEditProduct')) ||
    canEditAllProductFields || canEditPrices || canEditSpecifications || canEditLocations || canEditPhotos || editableProductFields.length > 0

  return {
    canEditProduct,
    canEditSku: toBoolean(getPermissionValue(permissions, 'can_edit_sku', 'canEditSku')),
    canViewCosts: toBoolean(getPermissionValue(permissions, 'can_view_costs', 'canViewCosts')),
    canEditAllProductFields,
    canEditSourcingPic: toBoolean(getPermissionValue(permissions, 'can_edit_sourcing_pic', 'canEditSourcingPic')),
    canEditProductCuratorPic: canEditProductCuratorPicValue === undefined
      ? canEditAllProductFields || editableProductFields.includes('product_curator_pic')
      : toBoolean(canEditProductCuratorPicValue),
    editableProductFields,
    canEditPrices,
    editablePriceFields,
    canEditBuyingPrice  : editablePriceFields.includes('buying_price'),
    canEditDefaultPrice : editablePriceFields.includes('default_price'),
    canEditSellingPrice : editablePriceFields.includes('selling_price'),
    canEditSpecifications,
    canEditLocations,
    canEditPhotos,
    isReadOnly: !canEditProduct
  }
}
